import { useId } from 'react'
import { useI18n } from '../i18n/index.js'

const DISPLAY = { fontFamily: 'var(--font-display)' }

/** Pawn silhouette, base at point (0, 0), height 164. */
const PAWN = {
  body: 'M-24 -100C-24 -70-32-46-44-24H44C32-46 24-70 24-100Z',
  collar: { x: -30, y: -112, width: 60, height: 14, rx: 7 },
  head: { cx: 0, cy: -138, r: 26 },
  base: { x: -50, y: -24, width: 100, height: 24, rx: 8 },
}

/**
 * Logo: two fanned cards and a pawn. `tile` adds the rounded background
 * (app icon); without it, the mark sits on the page background.
 */
export function LogoMark({ size = 28, tile = false, title }: { size?: number; tile?: boolean; title?: string }) {
  return (
    <svg
      className="logo-mark"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {tile && <rect width="64" height="64" rx="14" fill="#131317" />}
      <rect x="10" y="12" width="26" height="38" rx="5" fill="#6e5a34" transform="rotate(-28 23 31)" />
      <g transform="rotate(-10 25 31)">
        <rect x="12" y="11" width="26" height="38" rx="5" fill="#c9a55c" />
        <ellipse cx="25" cy="30" rx="8" ry="14" fill="#fff" opacity="0.22" transform="rotate(22 25 30)" />
      </g>
      <g fill="#ece7df" style={{ stroke: tile ? '#131317' : 'var(--bg, #0c0c0f)' }} strokeWidth="2.2" strokeLinejoin="round">
        <path d="M35.5 34.5c0 5.5-2 10-5 13.5h19c-3-3.5-5-8-5-13.5z" />
        <rect x="33.5" y="30.5" width="13" height="4.2" rx="2.1" />
        <circle cx="40" cy="24" r="6.6" />
        <rect x="27.5" y="47.5" width="25" height="6.5" rx="2.6" />
      </g>
    </svg>
  )
}

/** Illustration card: body, central oval, value in the center and in the corners. */
function ArtCard({
  cx,
  cy,
  angle,
  fill,
  ink,
  label,
  stroke,
  back = false,
  center = true,
}: {
  cx: number
  cy: number
  angle: number
  fill: string
  ink: string
  label?: string
  stroke?: string
  back?: boolean
  /** Central value: only for the front card (the others show their corner). */
  center?: boolean
}) {
  const w = 100
  const h = 146
  return (
    <g transform={`translate(${cx} ${cy}) rotate(${angle})`}>
      <rect x={-w / 2} y={-h / 2} width={w} height={h} rx="12" fill={fill} stroke={stroke ?? 'none'} strokeWidth="1.5" />
      {back ? (
        <>
          <rect x={-w / 2 + 9} y={-h / 2 + 9} width={w - 18} height={h - 18} rx="7" fill="none" stroke="#c9a55c" strokeOpacity="0.55" strokeDasharray="5 4" />
          <ellipse rx="26" ry="40" fill="none" stroke="#c9a55c" strokeOpacity="0.5" transform="rotate(20)" />
        </>
      ) : (
        <>
          <ellipse rx="30" ry="48" fill={ink} opacity="0.1" transform="rotate(20)" />
          {center && (
            <text y="17" textAnchor="middle" style={DISPLAY} fontSize="48" fontWeight="500" fill={ink}>
              {label}
            </text>
          )}
          <text x={-w / 2 + 11} y={-h / 2 + 22} style={DISPLAY} fontSize="15" fill={ink} opacity="0.8">
            {label}
          </text>
          <text x={w / 2 - 11} y={h / 2 - 11} textAnchor="end" style={DISPLAY} fontSize="15" fill={ink} opacity="0.8">
            {label}
          </text>
        </>
      )}
    </g>
  )
}

/** Home illustration: a pawn in front of three fanned cards. Understated, no heavy animation. */
export function HeroArt() {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const { t } = useI18n()
  return (
    <svg className="hero-art" viewBox="0 0 360 260" role="img" aria-label={t.landing.heroAlt}>
      <defs>
        <radialGradient id={`glow-${id}`} cx="50%" cy="55%" r="50%">
          <stop offset="0" stopColor="#c9a55c" stopOpacity="0.16" />
          <stop offset="1" stopColor="#c9a55c" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`pawn-${id}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#f4efe6" />
          <stop offset="0.65" stopColor="#e4ddd0" />
          <stop offset="1" stopColor="#bdb4a4" />
        </linearGradient>
      </defs>
      <ellipse cx="185" cy="140" rx="175" ry="120" fill={`url(#glow-${id})`} />
      <ellipse cx="200" cy="236" rx="130" ry="10" fill="#000" opacity="0.35" />
      <g className="hero-cards">
        <ArtCard cx={128} cy={126} angle={-20} fill="#17171c" ink="#c9a55c" stroke="rgba(255,255,255,0.12)" back />
        <ArtCard cx={166} cy={114} angle={-6} fill="#c9a55c" ink="#1a1612" label="+2" center={false} />
        <ArtCard cx={208} cy={124} angle={9} fill="#ece7df" ink="#2a2620" label="7" />
      </g>
      <g transform="translate(266 232)" fill={`url(#pawn-${id})`} stroke="#0c0c0f" strokeWidth="3" strokeLinejoin="round">
        <path d={PAWN.body} />
        <rect {...PAWN.collar} />
        <circle {...PAWN.head} />
        <rect {...PAWN.base} />
      </g>
    </svg>
  )
}
